import React from 'react';
import ReactTooltip from 'react-tooltip';
import { Loader, Collapsible, BaasicCheckbox, BaasicHeader } from 'core/components';
import { observer } from 'mobx-react';
import { defaultTemplate } from 'core/hoc';
import { isSome } from 'core/utils';
import {
	FulfillmentOrderItemsList,
	FulfillmentOrderHistoryLogList,
	FulfillmentOrderErrorLogList,
} from 'modules/order/pages';
import {
	onAssignStatuses,
	onHoldStatuses,
	onCancelStatuses,
	onPullLabelStatuses,
	onChangeShippingStatuses,
	onProductionModeStatuses,
	onNeverShippedStatuses,
	onReoptimizeStatuses,
	onHoldReason,
	onMarkAsShippedStatuses,
	onSplitFulfillmentOrderStatuses,
	channelType,
	voidLabelStatuses,
	shipmentInfoStatusEnum,
	carrierTypes,
} from 'common/constants';
import { CostBreakdownTooltip } from 'common/tooltips';
import ResizeObserver from 'react-resize-observer';
import { translate, formatDate } from 'core/utils';
import _ from 'lodash';

const OrderDetailTemplate = ({
	orderDetailViewStore,
	orderDetailViewStore: {
		canCancel,
		loading,
		rootStore,
		fulfillmentOrderItems,
		activeShipmentInfo,
		pdfURL,
		printLabel,
		printPackingSlip,
		isPrintingPackingSlipAllowed,
		fulfillmentOrderStore: { item },
		isSeller,
		isSuperAdminOrganization,
		displayPopoverButton,
		itemPrice,
		order,
		isVendorOrder,
		getWarehouseCodesForDisplay,
		isReentered,
		orderAddressData,
		initialize,
	},
	t,
}) => {
	if (loading) return <Loader />;
	return (
		<React.Fragment>
			<ReactTooltip />
			<div className="order">
				<BaasicHeader title="ORDERDETAILVIEW.ORDERDETAILS" rootStore={rootStore}>
					<ResizeObserver
						onResize={rect => {
							displayPopoverButton(rect.width);
						}}
					/>
					<ActionsRender viewStore={orderDetailViewStore} t={t} />
				</BaasicHeader>
				<div className="order__wrapper">
					<div className="order__header">
						<div className="container--fluid">
							<div className="order__header__wrapper">
								<div className="order__header__column">
									<div className="order__header__item">
										<h4 className="order__header__title type--base">{t('ORDERDETAILVIEW.FULFILLMENTORDERID')}</h4>
										<p data-cy={'fulfillmentOrderId'}>{item.fulfillmentOrderId}</p>
									</div>
								</div>
								{item.internalNote != '' && item.internalNote != null && (
									<div className="order__header__column">
										<div className="order__header__item">
											<h4 className="order__header__title type--base">{t('ORDERDETAILVIEW.NOTE')}</h4>
											<p data-cy={'internalNote'}>{item.internalNote}</p>
										</div>
									</div>
								)}
								<div className="order__header__column">
									<div className="order__header__item">
										<h4 className="order__header__title type--base">{t('ORDERDETAILVIEW.ORDERNUMBER')}</h4>
										{(isSeller || isSuperAdminOrganization) &&
										item.order.channel.channelType.abrv === channelType.amazon.toLowerCase() ? (
											<a
												href={'https://sellercentral.amazon.com/orders-v3/order/' + item.order.orderNumber}
												target="_blank"
											>
												{item.order.orderNumber}
											</a>
										) : (
											<p data-cy={'orderNumber'}>{item.order.orderNumber}</p>
										)}
									</div>
								</div>
								<div className="order__header__column">
									<div className="order__header__item">
										<h4 className="order__header__title type--base">{t('ORDERDETAILVIEW.FULFILLMENTORDERSTATUS')}</h4>
										<p data-cy={'status'}>{item.fulfillmentOrderStatus.name}</p>
									</div>
								</div>
								{item.cancelReason != null && (
									<div className="order__header__column">
										<div className="order__header__item">
											<h4 className="order__header__title type--base">{t('ORDERDETAILVIEW.CANCELREASON')}</h4>
											<p data-cy={'cancelReason'}>{item.cancelReason ? item.cancelReason.name : '-'}</p>
										</div>
									</div>
								)}
								{item.replacementReason != null && (
									<div className="order__header__column">
										<div className="order__header__item">
											<h4 className="order__header__title type--base">{t('ORDERDETAILVIEW.REPLACEMENTREASON')}</h4>
											<p data-cy={'replacementReason'}>{item.replacementReason ? item.replacementReason.name : '-'}</p>
										</div>
									</div>
								)}
								{activeShipmentInfo != null && (
									<div className="order__header__column">
										<div className="order__header__item">
											<h4 className="order__header__title type--base">{t('ORDERDETAILVIEW.TRACKINGNUMBER')}</h4>
											<p data-cy={'headerTrackingNumber'}>{activeShipmentInfo.trackingNumber}</p>
										</div>
									</div>
								)}
								{/* On Hold reason */}
								<div className="order__header__column">
									<div className="order__header__item">
										<h4 className="order__header__title type--base">{t('ORDERDETAILVIEW.ONHOLDREASON')}</h4>
										<p data-cy={'onHoldReason'}>{item.onHoldReason ? item.onHoldReason.name : '-'}</p>
									</div>
								</div>
								{activeShipmentInfo != null && (
									<div className="order__header__column">
										<div className="order__header__item">
											<h4 className="order__header__title type--base">{t('ORDERDETAILVIEW.ISBUYSHIPPING')}</h4>
											<p>
												{activeShipmentInfo.isBuyShipping ? (
													<i data-cy={'isBuyShippingTrue'} className="type--color--success fas fa-check-circle" />
												) : (
													<i data-cy={'isBuyShippingFalse'} className="fas fa-times-circle"></i>
												)}
											</p>
										</div>
									</div>
								)}
								<div className="order__header__column">
									<div className="order__header__item">
										<h4 className="order__header__title type--base">{t('ORDERDETAILVIEW.ISPRIME')}</h4>
										<p>
											{item.order.isPrime ? (
												<i data-cy={'isPrimeTrue'} className="icon icon--xlrg icon--prime" />
											) : (
												<i data-cy={'isPrimeFalse'} className="icon icon--xlrg icon--prime--grey" />
											)}
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div className="order__details">
						<div className="container--fluid">
							<div className="row">
								<div className="col col-sml-12">
									<div className="order__box">
										<h4 className="order__box__title">{t('GENERALINFORMATION')}</h4>
										<div className="order__box__content">
											<div
												className={
													item.productionMode
														? 'flex__wrapper flex__wrapper--justify--between is-warning'
														: 'flex__wrapper flex__wrapper--justify--between'
												}
											>
												<div className="flex__column">
													<div>
														<div className="order__header__item">
															<h4 className="order__header__title">{t('ORDERDETAILVIEW.CUSTOMER')}</h4>
														</div>
														<div className="order__box__content">
															{orderAddressData.buyerName != null && (
																<span>
																	<p className="order__label">{t('ORDERDETAILVIEW.CUSTOMERNAME')}</p>
																	<p data-cy={'customerBuyerName1'} className="fs-mask">
																		{orderAddressData.buyerName}
																	</p>
																</span>
															)}

															<p className="order__label spc--top--sml">{t('ORDERDETAILVIEW.EMAIL')}</p>
															<p data-cy={'customerCustomerEmail'}>{orderAddressData.buyerEmail}</p>

															<p className="order__label spc--top--sml">
																<span>{t('ORDERDETAILVIEW.ADDRESS')}</span>
																{orderAddressData.addressChanged && (
																	<i
																		data-tip={t('ORDERVIEW.ADDRESSCHANGED')}
																		className="icon icon--xmed icon--location-check--primary spc--left--nano"
																	></i>
																)}
															</p>
															<p data-cy={'customerBuyerName2'} className="fs-mask">
																{orderAddressData.buyerName}
															</p>
															<p data-cy={'customerShippingAddress1'} className="fs-mask">
																{orderAddressData.shippingAddress1}
															</p>
															{orderAddressData.shippingAddress2 != '' && (
																<p data-cy={'customerShippingAddress2'} className="fs-mask">
																	{orderAddressData.shippingAddress2}
																</p>
															)}
															{orderAddressData.shippingAddress3 != '' && (
																<p data-cy={'customerShippingAddress3'} className="fs-mask">
																	{orderAddressData.shippingAddress3}
																</p>
															)}
															<div>
																{/* separate spans are used for easier identification in frontend tests */}
																<span data-cy={'customerCity'} id="customer-city">
																	{orderAddressData.shippingCity}
																</span>
																<span>, </span>
																<span data-cy={'customerState'} id="customer-state">
																	{orderAddressData.shippingCoreCountryState
																		? orderAddressData.shippingCoreCountryState.name
																		: null}
																</span>
																<span> </span>
																<span data-cy={'customerZipCode'} id="customer-zipCode">
																	{orderAddressData.shippingZipCode}
																</span>
															</div>
															{orderAddressData.buyerPhone != null && (
																<span>
																	<p className="order__label spc--top--sml">{t('ORDERDETAILVIEW.PHONE')}</p>
																	<p data-cy={'customerBuyerPhone'} className="fs-mask">
																		{orderAddressData.buyerPhone}
																	</p>
																</span>
															)}
														</div>
													</div>
												</div>
												<div className="flex__column">
													<div>
														<div className="order__header__item">
															<h4 className="order__header__title">{t('ORDERDETAILVIEW.SHIPPING')}</h4>
														</div>
														<div className="order__box__content">
															<p className="order__label">
																<span>{t('ORDERDETAILVIEW.ADDRESS')}</span>
																{orderAddressData.addressChanged && (
																	<i
																		data-tip={t('ORDERVIEW.ADDRESSCHANGED')}
																		className="icon icon--xmed icon--location-check--primary spc--left--nano"
																	></i>
																)}
															</p>
															<p data-cy={'shippingBuyerName'} className="fs-mask">
																{orderAddressData.buyerName}
															</p>
															<p data-cy={'shippingShippingAddress1'} className="fs-mask">
																{orderAddressData.shippingAddress1}
															</p>
															{orderAddressData.shippingAddress2 != '' && (
																<p data-cy={'shippingShippingAddress2'} className="fs-mask">
																	{orderAddressData.shippingAddress2}
																</p>
															)}
															{orderAddressData.shippingAddress3 != '' && (
																<p data-cy={'shippingShippingAddress3'} className="fs-mask">
																	{orderAddressData.shippingAddress3}
																</p>
															)}
															<div>
																{/* separate spans are used for easier identification in frontend tests */}
																<span data-cy={'shippingCity'} id="shipping-city">
																	{orderAddressData.shippingCity}
																</span>
																<span>, </span>
																<span data-cy={'shippingState'} id="shipping-state">
																	{orderAddressData.shippingCoreCountryState
																		? orderAddressData.shippingCoreCountryState.name
																		: null}
																</span>
																<span> </span>
																<span data-cy={'shippingZipCode'} id="shipping-zipCode">
																	{orderAddressData.shippingZipCode}
																</span>
															</div>
															{orderAddressData.buyerPhone != null && (
																<span>
																	<p className="order__label spc--top--sml">{t('ORDERDETAILVIEW.PHONE')}</p>
																	<p data-cy={'shippingBuyerPhone'} className="fs-mask">
																		{orderAddressData.buyerPhone}
																	</p>
																</span>
															)}
															<p className="order__label spc--top--sml">
																{t('ORDERDETAILVIEW.ASSIGNEDCARRIERSERVICE')}
															</p>
															<p data-cy={'assignedCarrierService'}>
																{item.carrierService ? item.carrierService.name : '-'}
															</p>

															<span>
																<p className="order__label spc--top--sml">{t('ORDERDETAILVIEW.ASSIGNEDCARRIER')}</p>
																<p data-cy={'assignedCarrier'}>{item.carrier ? item.carrier.name : '-'}</p>
															</span>

															<p className="order__label spc--top--sml">{t('ORDERDETAILVIEW.SHIPBYDATE')}</p>
															{isReentered && (
																<React.Fragment>
																	{formatDate(item.newShipByDate, false, 'L')}
																	<i
																		data-tip={t('TOOLTIP.NEWSHIPBYDATE', {
																			date: formatDate(item.shipByDate, false, 'L'),
																		})}
																		data-cy={'newShipByDateTooltip'}
																		className="spc--left--tny fas fa-exclamation-circle"
																	/>
																</React.Fragment>
															)}
															{!isReentered && <p data-cy={'shipByDate'}>{formatDate(item.shipByDate, false, 'L')}</p>}

															{activeShipmentInfo && (
																<span>
																	<p className="order__label spc--top--sml">{t('ORDERDETAILVIEW.SHIPPINGCOST')}</p>
																	{activeShipmentInfo.shippingCostSurcharge != 0 ? (
																		<React.Fragment>
																			<p
																				data-cy={'shippingCost'}
																				className="order__highlight"
																			>{`$${activeShipmentInfo.shippingCostTotal}`}</p>
																			<CostBreakdownTooltip
																				cypressId={'shippingShippingCostTooltip'}
																				idValue={activeShipmentInfo.id}
																				shippingCost={activeShipmentInfo.cost}
																				surplusCharge={activeShipmentInfo.shippingCostSurcharge}
																				total={activeShipmentInfo.shippingCostTotal}
																				displayIcon={true}
																			/>
																		</React.Fragment>
																	) : (
																		<p>{`$${activeShipmentInfo.cost}`}</p>
																	)}
																</span>
															)}
															<span>
																<p className="order__label spc--top--sml">{t('ORDERDETAILVIEW.ASSIGNEDRATE')}</p>
																{isSome(item.assignedRate) ? (
																	!(item.assignedRateSurcharge == null || item.assignedRateSurcharge == 0) ? (
																		<React.Fragment>
																			<p data-cy={'assignedRate'} className="order__highlight">
																				{' '}
																				{`$${item.assignedRateTotal}`}
																			</p>
																			<CostBreakdownTooltip
																				cypressId={'shippingAssignedRateTooltip'}
																				idValue={item.id}
																				shippingCost={item.assignedRate}
																				surplusCharge={item.assignedRateSurcharge}
																				total={item.assignedRateTotal}
																				displayIcon={true}
																			/>
																		</React.Fragment>
																	) : (
																		<p>{`$${item.assignedRate}`}</p>
																	)
																) : (
																	<p>{'-'}</p>
																)}
															</span>
															<span>
																<p data-cy={'shippingZone'} className="order__label spc--top--sml">
																	{t('ORDERDETAILVIEW.ZONE')}
																</p>
																{activeShipmentInfo ? activeShipmentInfo.zone : '-'}
															</span>
														</div>
													</div>
												</div>
												<div className="flex__column">
													<div>
														<div className="order__header__item">
															<h4 className="order__header__title">{t('ORDERDETAILVIEW.WAREHOUSE')}</h4>
														</div>
														<div className="order__box__content">
															<p className="order__label">{t('ORDERDETAILVIEW.WAREHOUSENAME')}</p>
															<p data-cy={'warehouseWarehouseName'}>
																{item.warehouseOrganization ? item.warehouseOrganization.applicationTitle : '-'}
															</p>

															<p className="order__label spc--top--sml">{t('ORDERDETAILVIEW.CODE')}</p>
															<p data-cy={'warehouseCode'}>
																{item.warehouseOrganization && item.warehouseOrganization.convertWarehouses
																	? getWarehouseCodesForDisplay(item)
																	: '-'}
															</p>
														</div>
														{isVendorOrder && (
															<div>
																<div className="order__header__item">
																	<h4 className="order__header__title">{t('ORDERDETAILVIEW.REQUIREDSHIPPING')}</h4>
																</div>
																<div className="order__box__content">
																	<p className="order__label">{t('ORDERDETAILVIEW.REQUIREDWAREHOUSE')}</p>
																	<p data-cy={'requiredWarehouse'}> {item.order.requiredWarehouse}</p>

																	<p className="order__label spc--top--sml">
																		{t('ORDERDETAILVIEW.REQUIREDCARRIERSERVICE')}
																	</p>
																	<p data-cy={'requiredCarrierService'}> {item.order.requiredCarrierService}</p>
																</div>
															</div>
														)}
													</div>
												</div>
												<div className="flex__column flex__column--order">
													<div>
														<div className="order__header__item">
															<h4 className="order__header__title">{t('ORDERDETAILVIEW.MASTERORDER')}</h4>
														</div>
														<div className="order__box__content">
															<p className="order__label">{t('ORDERDETAILVIEW.ORDERDATE')}</p>
															<p data-cy={'orderDate'}>{formatDate(item.order.orderDate, true, 'LLL')}</p>

															{item.order.customerServiceOrderNumber != '' && (
																<React.Fragment>
																	<div className="separator--primary spc--top--sml spc--bottom--sml"></div>
																	<p className="order__label">{t('ORDERDETAILVIEW.CUSTOMERSERVICEORDERNUMBER')}</p>
																	<p data-cy={'customerServiceOrderNumber'}>{item.order.customerServiceOrderNumber}</p>
																</React.Fragment>
															)}

															{(item.order.ref1 || item.order.ref2 || item.order.ref3) && (
																<React.Fragment>
																	<div className="separator--primary spc--top--sml spc--bottom--sml"></div>
																	{item.order.ref1 && (
																		<React.Fragment>
																			<p className="order__label">{t('ORDERDETAILVIEW.REF1')}</p>
																			<p>{item.order.ref1}</p>
																		</React.Fragment>
																	)}
																	{item.order.ref2 && (
																		<React.Fragment>
																			<p className="order__label">{t('ORDERDETAILVIEW.REF2')}</p>
																			<p>{item.order.ref2}</p>
																		</React.Fragment>
																	)}
																	{item.order.ref3 && (
																		<React.Fragment>
																			<p className="order__label">{t('ORDERDETAILVIEW.REF3')}</p>
																			<p>{item.order.ref3}</p>
																		</React.Fragment>
																	)}
																</React.Fragment>
															)}

															{item.order.source != '' && (
																<React.Fragment>
																	<div className="separator--primary spc--top--sml spc--bottom--sml"></div>
																	<p data-cy={'channelSource'} className="order__label">
																		{t('ORDERDETAILVIEW.CHANNELSOURCE')}
																	</p>
																	<p>{item.order.source}</p>
																</React.Fragment>
															)}

															<div className="separator--primary spc--top--sml spc--bottom--sml"></div>

															<div className="row">
																<div className="col col-sml-6">
																	<p className="order__label">{t('ORDERDETAILVIEW.ITEMPRICE')}</p>
																</div>
																<div className="col col-sml-6">
																	<p data-cy={'itemPrice'} className="type--right">
																		{itemPrice}
																	</p>
																</div>
															</div>

															<div className="row spc--top--sml">
																<div className="col col-sml-6">
																	<p className="order__label">{t('ORDERDETAILVIEW.TOTALTAX')}</p>
																</div>
																<div className="col col-sml-6">
																	<p data-cy={'totalTax'} className="type--right">
																		{order.taxPaid}
																	</p>
																</div>
															</div>
															<div className="row spc--top--sml">
																<div className="col col-sml-6">
																	<p className="order__label">{t('ORDERDETAILVIEW.TOTALSHIPPING')}</p>
																</div>
																<div className="col col-sml-6">
																	<p data-cy={'totalShipping'} className="type--right">
																		{order.shippingPaid}
																	</p>
																</div>
															</div>
															<div className="row spc--top--sml">
																<div className="col col-sml-6">
																	<p className="order__label">{t('ORDERDETAILVIEW.SUBTOTAL')}</p>
																</div>
																<div className="col col-sml-6">
																	<p data-cy={'subtotal'} className="type--right">
																		{itemPrice}
																	</p>
																</div>
															</div>
															<div className="row spc--top--sml">
																<div className="col col-sml-6">
																	<p className="order__label">{t('ORDERDETAILVIEW.DISCOUNT')}</p>
																</div>
																<div className="col col-sml-6">
																	<p data-cy={'discount'} className="type--right">
																		{order.discount * -1}
																	</p>
																</div>
															</div>
															<div className="row spc--top--sml">
																<div className="col col-sml-6">
																	<p className="order__label type--lrg">{t('ORDERDETAILVIEW.TOTALPAID')}</p>
																</div>
																<div className="col col-sml-6">
																	<p data-cy={'totalPaid'} className="type--right type--lrg">
																		{order.amountPaid}
																	</p>
																</div>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
							{item.shipmentInfos.length > 0 &&
								_.every(item.shipmentInfos, shipmentInfo => {
									return shipmentInfo.trackingNumber.length !== 0;
								}) && (
									<div className="row spc--top--sml">
										<div className="col col-sml-12">
											<div className="order__box">
												<h4 className="order__box__title">{t('ORDERDETAILVIEW.LABELS')}</h4>
												<div className="order__box__content">
													<div className="flex__wrapper flex__wrapper--justify--between">
														<div className="flex__column">
															{item.shipmentInfos.length > 0 && (
																<span className="order__label">{t('ORDERDETAILVIEW.TRACKINGNUMBERS')}:</span>
															)}
															{_.map(item.shipmentInfos, (label, idx) => {
																if (label.status == shipmentInfoStatusEnum.voided) {
																	return (
																		<p
																			data-cy={'voidedTrackingNumbers'}
																			key={idx}
																			style={
																				label.status == shipmentInfoStatusEnum.voided
																					? { textDecoration: 'line-through', color: 'grey' }
																					: {}
																			}
																		>
																			{label.trackingNumber}
																		</p>
																	);
																} else {
																	return isSome(item.carrierService) ? (
																		<p data-cy={'activeTrackingNumber'} key={idx}>
																			{console.log(item)}
																			{_.get(item, 'order.channel.name') !== 'LTL Freight' &&
																			_.get(item, 'order.requiredCarrierService') !== 'LTL' ? (
																				<a
																					href={
																						item.carrierService.carrierType.name === carrierTypes.passport
																							? `https://track${
																									process.env.NODE_ENV === 'production' ? '' : '-stg'
																							  }.passportshipping.com/${label.trackingNumber}`
																							: item.carrierService.carrierType.name === carrierTypes.usps
																							? 'https://www.stamps.com/tracking-details/?t=' + label.trackingNumber
																							: item.carrierService.carrierType.name === carrierTypes.fedex
																							? 'https://www.fedex.com/apps/fedextrack/?action=track&language=english&tracknumbers=' +
																							  label.trackingNumber
																							: 'https://www.ups.com/track?loc=en_US&requester=QUIC&tracknum=' +
																							  label.trackingNumber.split('-')[0]
																					}
																					target="_blank"
																				>
																					{label.trackingNumber}
																				</a>
																			) : (
																				<React.Fragment>{label.trackingNumber}</React.Fragment>
																			)}
																		</p>
																	) : (
																		<p key={idx}>{label.trackingNumber}</p>
																	);
																}
															})}
															{/* Print Label */}
															{pdfURL && !canCancel && (
																<button
																	data-cy={'printLabel'}
																	className="btn btn--med btn--primary spc--top--med spc--right--sml"
																	type="button"
																	onClick={printLabel}
																>
																	{t('PRINTLABEL')}
																</button>
															)}
															{isPrintingPackingSlipAllowed && (
																<button
																	data-cy={'printPackingSlip'}
																	className="btn btn--med btn--primary spc--top--med spc--right--sml"
																	type="button"
																	onClick={printPackingSlip}
																>
																	{t('PRINTPACKINGSLIP')}
																</button>
															)}
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								)}
						</div>
						<div data-cy={'items'} className="order__box--no-padd spc--top--med">
							<Collapsible expanded={true} title={t('ORDERDETAILVIEW.FULFILLMENTORDERITEMS')}>
								<FulfillmentOrderItemsList data={fulfillmentOrderItems.slice()} />
							</Collapsible>
						</div>
						<div data-cy={'viewActivity'} className="spc--top--med">
							<Collapsible title={translate(['FULFILLMENTORDERVIEW.HISTORYLOG.VIEWACTIVITY'])}>
								<FulfillmentOrderHistoryLogList orderId={item.fulfillmentOrderId} />
							</Collapsible>
						</div>
					</div>
				</div>
			</div>
			<iframe id="pdf-print" name="pdf-print" src={pdfURL} style={{ display: 'none' }}></iframe>
		</React.Fragment>
	);
};

const ActionsRender = observer(({ viewStore, t }) => {
	const { shouldDisplayActionButton } = viewStore;
	return (
		<div className="layout__title--flex--end">
			{shouldDisplayActionButton == false ? (
				<Actions viewStore={viewStore} t={t} />
			) : (
				<div className="popover" onClick={() => (viewStore.actionMenuOpen = !viewStore.actionMenuOpen)}>
					<button className={'popover__button popover__button--header' + (viewStore.actionMenuOpen ? ' active' : '')}>
						<i className="fa fa-cogs spc--right--tny" />
						<span className="spc--right--tny">{t('ACTIONS')}</span>
						<i className="fa fa-angle-down spc--left--sml" />
					</button>
					<div className={'popover__menu w--220--px' + (viewStore.actionMenuOpen ? ' active' : '')}>
						<ul>
							<Actions viewStore={viewStore} t={t} />
						</ul>
					</div>
				</div>
			)}
		</div>
	);
});

const Actions = observer(({ viewStore, t }) => {
	const {
		shouldDisplayActionButton,
		openModal,
		openAssignmentModal,
		openOnHoldModal,
		openChangeShippingModal,
		openMarkAsShippedModal,
		openEditOrderModal,
		isWarehouseAdministrator,
		hasLabel,
		isSeller,
		fulfillmentOrder,
		rootStore,
		fulfillmentOrderStore: { item },
		isSuperAdminOrganization,
		getProductionModeConfirmModalProps,
		isBoxRequired,
		isVendorOrder,
		isLtlOrder,
		openSplitFulfillmentOrderModal,
		editFulfillmentOrderSplit,
		getCancelConfirmModalProps,
		openConfirmationModal,
		getPullLabelConfirmModalProps,
		getReoptimizeConfirmModalProps,
		openVoidLabelConfirmModal,
		openNeverShippedModal,
		timezone,
		format,
	} = viewStore;
	return (
		<React.Fragment>
			{item.fulfillmentOrderErrors.length > 0 && (
				<div className="popover__menu__item">
					<button
						className="btn btn--white"
						type="button"
						onClick={() =>
							openModal({
								data: {
									orderId: item.fulfillmentOrderId,
									errorsList: item.fulfillmentOrderErrors,
									t,
									timezone,
									format,
								},
								component: FulfillmentOrderErrorLogList,
							})
						}
					>
						<i
							data-cy={'viewItemErrorLog'}
							title={t('ERRORLOG')}
							className="fa fa-exclamation-circle spc--right--tny k-custom-clickable"
						></i>
						{t('ERRORLOG')}
					</button>
				</div>
			)}
			{/* Pull Label */}
			{(isWarehouseAdministrator || isSeller || isSuperAdminOrganization) &&
				!isBoxRequired &&
				!hasLabel &&
				rootStore.authStore.hasPiiAccess &&
				_.findIndex(onPullLabelStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 && (
					<div className="popover__menu__item">
						<button
							data-cy={'pullLabel'}
							className="btn btn--white"
							onClick={() => openConfirmationModal(getPullLabelConfirmModalProps())}
						>
							<i title={t('PULLLABEL')} className="far fa-arrow-alt-circle-down spc--right--sml k-custom-clickable"></i>
							{t('PULLLABEL')}
						</button>
					</div>
				)}
			{/* Assign Carrier and Warehouse */}
			{_.findIndex(onAssignStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 && (
				<div className="popover__menu__item">
					<button
						data-cy={'assignCarrierAndWarehouse'}
						className="btn btn--white"
						type="button"
						onClick={openAssignmentModal}
					>
						<i title={t('ASSIGN')} className="fa fa-tag spc--right--tny k-custom-clickable"></i>
						{t('FULFILLMENTORDER.ASSIGNMODAL.TITLE')}
					</button>
				</div>
			)}
			{/* On Hold */}
			{(isSeller || isSuperAdminOrganization) &&
				_.findIndex(onHoldStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 && (
					<div className="popover__menu__item">
						<button data-cy={'onHold'} className="btn btn--white" onClick={openOnHoldModal}>
							<i title={t('ONHOLD')} className="fa fa-hand-paper spc--right--tny k-custom-clickable"></i>
							{t('ONHOLDVIEW.ONHOLD')}
						</button>
					</div>
				)}
			{/* Change shipping */}
			{(isSeller || isSuperAdminOrganization) &&
				!isVendorOrder &&
				!isLtlOrder &&
				(item.onHoldReason == null || item.onHoldReason.abrv != onHoldReason.toBeCancelled) &&
				_.findIndex(onChangeShippingStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 && (
					<div className="popover__menu__item">
						<button data-cy={'changeShipping'} className="btn btn--white" onClick={openChangeShippingModal}>
							<i title={t('CHANGESHIPPING')} className="fa fa-exchange-alt spc--right--sml k-custom-clickable"></i>
							{t('CHANGESHIPPINGVIEW.TITLE')}
						</button>
					</div>
				)}
			{isSuperAdminOrganization &&
				((_.findIndex(onSplitFulfillmentOrderStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 &&
					(item.fulfillmentOrderItems.length > 1 || item.fulfillmentOrderItems[0].quantity > 1)) ||
					editFulfillmentOrderSplit) && (
					<div className="popover__menu__item">
						<button data-cy={'splitBySuperAdmin'} className="btn btn--white" onClick={openSplitFulfillmentOrderModal}>
							<i
								title={t(editFulfillmentOrderSplit ? 'EDITSPLITFULFILLMENTORDER' : 'SPLITFULFILLMENTORDER')}
								className="icon icon--grid-action icon--split--white spc--right--tny"
								onClick={openSplitFulfillmentOrderModal}
							></i>
							{t(editFulfillmentOrderSplit ? 'EDITSPLITFULFILLMENTORDER' : 'SPLITFULFILLMENTORDER')}
						</button>
					</div>
				)}
			{/* Cancel */}
			{isSuperAdminOrganization &&
				_.findIndex(onCancelStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 && (
					<div className="popover__menu__item">
						<button
							data-cy={'cancelFulfillmentOrder'}
							className="btn btn--white"
							onClick={() => openConfirmationModal(getCancelConfirmModalProps())}
						>
							<i
								title={t('CANCELFULFILLMENTORDER')}
								className="fa fa-window-close spc--right--tny k-custom-clickable"
							></i>
							{t('ORDERDETAILVIEW.CANCELFULFILLMENTORDER')}
						</button>
					</div>
				)}

			{/* Reoptimize */}
			{isSuperAdminOrganization &&
				_.findIndex(onReoptimizeStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 && (
					<div className="popover__menu__item">
						<button
							data-cy={'reoptimize'}
							className="btn btn--white"
							onClick={() => openConfirmationModal(getReoptimizeConfirmModalProps())}
						>
							<i
								title={t('REOPTIMIZE')}
								className="icon icon--grid-action icon--order-repeat--white spc--right--tny"
							></i>
							{t('REOPTIMIZE')}
						</button>
					</div>
				)}

			{/* Never Shipped */}
			{(isSeller || isSuperAdminOrganization) &&
				_.findIndex(onNeverShippedStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 && (
					<div className="popover__menu__item">
						<button data-cy={'neverShipped'} className="btn btn--white" onClick={openNeverShippedModal}>
							<i
								title={t('NEVERSHIPPED')}
								className="icon icon--grid-action icon--never-shipped--white spc--right--tny k-custom-clickable"
							></i>
							{t('NEVERSHIPPED')}
						</button>
					</div>
				)}
			{/* Mark as Shipped */}
			{(isSeller || isSuperAdminOrganization) &&
				_.findIndex(onMarkAsShippedStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 && (
					<div className="popover__menu__item">
						<button data-cy={'markAsShipped'} className="btn btn--white" onClick={openMarkAsShippedModal}>
							<i title={t('MARKASSHIPPED')} className="fa fa-truck spc--right--tny k-custom-clickable"></i>
							{t('MARKASSHIPPED')}
						</button>
					</div>
				)}
			{/* Edit Order */}
			{(isSeller || isSuperAdminOrganization) && !hasLabel && rootStore.authStore.hasPiiAccess && (
				<div className="popover__menu__item">
					<button
						data-cy={'editOrderInformation'}
						className="btn btn--white"
						type="button"
						onClick={openEditOrderModal}
					>
						<i title={t('EDIT')} className="fa fa-edit spc--right--tny k-custom-clickable"></i>
						{t('FULFILLMENTORDER.EDITMODAL.TITLE')}
					</button>
				</div>
			)}
			{/* Void Label */}
			{(isSeller || isSuperAdminOrganization) &&
			(isVendorOrder ? process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'staging' : true) && // Show VoidLabel for test environments for vendor
				!shouldDisplayActionButton &&
				hasLabel &&
				_.findIndex(voidLabelStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 && (
					<div className="popover__menu__item">
						<button data-cy={'voidLabel'} className="btn btn--white" onClick={() => openVoidLabelConfirmModal(true)}>
							<i title={t('VOIDLABEL')} className="fa fa-undo spc--right--tny k-custom-clickable"></i>
							{t('VOIDLABELVIEW.VOIDLABEL')}
						</button>
					</div>
				)}
			{/* Production Mode. */}
			{(isSeller || isSuperAdminOrganization) &&
				fulfillmentOrder.order.channel.channelType.abrv == 'amazon' &&
				rootStore.authStore.hasPiiAccess &&
				_.findIndex(onProductionModeStatuses, status => status == item.fulfillmentOrderStatus.abrv) != -1 &&
				process.env.NODE_ENV !== 'production' && (
					<div data-cy={'productionMode'} className="popover__menu__item--alt">
						<i title={t('ORDERVIEW.PRODUCTIONMODE')} className="icon icon--med icon--document-lock spc--right--tny"></i>
						<span>{t('ORDERVIEW.PRODUCTIONMODE')}</span>
						<div className="display--ib align--v--middle spc--left--tny">
							<BaasicCheckbox
								className={'input--toggle input--toggle--production'}
								id={'production-mode-toggle'}
								checked={fulfillmentOrder.productionMode}
								onChangeCallback={event =>
									openConfirmationModal(getProductionModeConfirmModalProps(event.currentTarget.checked))
								}
							/>
						</div>
					</div>
				)}
		</React.Fragment>
	);
});

export default defaultTemplate(OrderDetailTemplate);



// WEBPACK FOOTER //
// ./src/themes/default/modules/order/pages/OrderDetailTemplate.jsx